import { ButtonGroup, Theme, createStyles, makeStyles } from '@forgeglobal/design-framework'
import {
  OpportunityFragment,
  TradeIntentStatusType,
  TradeIntentType,
  useGetAllOpportunitiesQuery,
} from 'graphql/bindings/AdminBindings'
import React, { Fragment, useEffect, useState } from 'react'

import { OpportunityFormCards, OpportunityFormCardsProps } from './OpportunityFormCards'
import moment from 'moment'

interface OpportunityFormsProps {
  accountId: string
  refetchOnCancelMutation?: OpportunityFormCardsProps['refetchOnCancelMutation']
}

const useStyles = makeStyles(({ spacing }: Theme) =>
  createStyles({
    buttonGroupContainer: {
      width: '100%',
      margin: spacing(2, 0),
      display: 'flex',
      justifyContent: 'flex-end',
    },
    formContainer: {
      marginBottom: spacing(4),
    },
  })
)

export const OpportunityForms: React.FC<OpportunityFormsProps> = ({ accountId, refetchOnCancelMutation }) => {
  const [filter, setFilter] = useState('Active')
  const classes = useStyles()
  const [orderOfOpportunitiesById, setOrder] = useState<string[]>([])
  const { data } = useGetAllOpportunitiesQuery({
    variables: { accountId: accountId },
  })

  const opportunities = data?.account?.opportunities ?? []

  const orderedOpportunities = opportunities.sort((a: OpportunityFragment, b: OpportunityFragment) => {
    return moment(a.last_refreshed_at).isBefore(moment(b.last_refreshed_at)) ? 1 : -1
  })

  useEffect(() => {
    // lock down order of filteredOpportunities so that when they are edited return the same ordered list
    if (orderOfOpportunitiesById.length !== opportunities.length) {
      setOrder(opportunities.map(opportunity => opportunity.id))
    }
  }, [opportunities, orderOfOpportunitiesById, setOrder])

  const sortedOpportunities = orderOfOpportunitiesById.reduce((sortedOpps, nextInLine) => {
    const nextOpp = orderedOpportunities.find(opportunity => opportunity.id === nextInLine)
    return nextOpp ? [...sortedOpps, nextOpp] : sortedOpps
  }, [] as OpportunityFragment[])

  const activeStatuses = [
    TradeIntentStatusType.PrePipeline,
    TradeIntentStatusType.Pipeline,
    TradeIntentStatusType.Qualified,
    TradeIntentStatusType.PreClosings,
  ]

  const isExpired: (o: OpportunityFragment) => boolean = opportunity => {
    if (!opportunity.expired_at) return false

    const now = moment()
    const expires = moment(opportunity.expired_at)

    return expires < now
  }

  const filteredOpportunities = sortedOpportunities.filter((opportunity: OpportunityFragment) => {
    const isActive = activeStatuses.includes(opportunity.status)
    const isCancelled = opportunity.status === TradeIntentStatusType.Cancelled

    switch (filter) {
      case 'Active':
        return isActive && !isExpired(opportunity)
      case 'Expired':
        return isCancelled || (isActive && isExpired(opportunity))
      default:
        return false
    }
  })

  const bids = filteredOpportunities.filter((opportunity: OpportunityFragment) => {
    return opportunity.type === TradeIntentType.Bid
  })

  const asks = filteredOpportunities.filter((opportunity: OpportunityFragment) => {
    return opportunity.type === TradeIntentType.Ask
  })

  const handleChange = (_: React.MouseEvent<HTMLElement>, value: string) => {
    if (value !== null && value !== filter) {
      setFilter(value)
    }
  }

  return (
    <Fragment>
      <div className={classes.buttonGroupContainer}>
        <ButtonGroup
          onChange={handleChange}
          value={filter}
          options={[
            { label: 'Active', value: 'Active' },
            { label: 'Expired', value: 'Expired' },
          ]}
          size={'small'}
          exclusive
        />
      </div>
      <div className={classes.formContainer}>
        {['Bid', 'Ask'].map(section => {
          return (
            <OpportunityFormCards
              filter={filter}
              key={section}
              type={section}
              opportunities={section === 'Bid' ? bids : asks}
              refetchOnCancelMutation={refetchOnCancelMutation}
            />
          )
        })}
      </div>
    </Fragment>
  )
}
